<template>
  <div class="content">
    <div class="response_table">
      <table>
        <thead>
          <tr>
            <th>Parameters</th>
            <th v-for="modem in modem_list" :key="modem">
              {{ simcard_name(modem) }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="row in param_rows" :key="row.key" class="param_line">
            <td>
              <router-link
                  v-if="row.key === 'apn' && $store.getters.getPermissions.permissionGod === true"
                  to="/simcard_profile_list/apn_list"
                  target="_blank"
              >
                {{ row.label }}
              </router-link>
              <template v-else>{{ row.label }}</template>
            </td>

            <td v-for="modem in modem_list" :key="row.key + '_' + modem">
              <!-- STCP -->
              <template v-if="row.key === 'stcp'">
                <div class="filter checkbox">
                  <label class="switch" @click.prevent="toggleSimple('stcp', modem)">
                    <input type="checkbox" v-model="settings.simcards.stcp[modem]">
                    <span class="slider round"></span>
                  </label>
                </div>
              </template>

              <!-- BACKUP -->
              <template v-if="row.key === 'backup'">
                <div class="filter checkbox">
                  <label class="switch" @click.prevent="toggleBackup(modem)">
                    <input type="checkbox" v-model="settings.simcards.backup[modem]">
                    <span class="slider round"></span>
                  </label>
                </div>
              </template>

              <!-- POWER -->
              <template v-else-if="row.key === 'power'">
                <div class="filter checkbox">
                  <label
                      class="switch"
                      @click.prevent="toggleSimple('power', modem)"
                      :class="{ 'no-user-access': !$store.getters.getPermissions?.permissionGod === true }"
                  >
                    <input type="checkbox" v-model="settings.simcards.power[modem]">
                    <span class="slider round"></span>
                  </label>
                </div>
              </template>

              <!-- ALLOW INTERNATIONAL -->
              <template v-else-if="row.key === 'allow_international'">
                <div class="filter checkbox">
                  <label class="switch" @click.prevent="toggleSimple('allow_international', modem)">
                    <input type="checkbox" v-model="settings.simcards.allow_international[modem]">
                    <span class="slider round"></span>
                  </label>
                </div>
              </template>

              <!-- APN -->
              <template v-else-if="row.key === 'apn'">
                <tree-select
                    v-if="can_edit_apn && apnOptions(modem).length"
                    v-model="settings.simcards.apn[modem]"
                    :options="apnOptions(modem)"
                    :placeholder="translate('apn')"
                    :clearable="true"
                />
                <span v-else-if="apnOptions(modem).length === 0 && settings.simcards.apn[modem]">
                  {{ settings.simcards.apn[modem] }}
                </span>
              </template>

              <!-- GPS -->
              <template v-else-if="row.key === 'gps'">
                <tree-select
                  v-model="settings.simcards.gps[modem]"
                  :options="gpsOptions(modem)"
                  :placeholder="translate('gps')"
                  :clearable="true"
                />
              </template>
            </td>
          </tr>

          <tr class="empty"></tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: "EditNetworkSettingsSimcards",
  props: ["network_settings"],

  data() {
    return {
      settings: {
        modems: [],
        simcards: {
          backup: {},
          power: {},
          allow_international: {},
          apn: {}
        },
        simcards_info: {},
        available_apns: {}
      }
    };
  },

  computed: {
    can_edit_apn() {
      const permissions = this.$store.getters.getPermissions
      return permissions.editDeviceApn === true
    },
    param_rows() {
      const rows = [];

      if (this.check_stcp) rows.push({ key: "stcp", label: "Stcp" });
      if (this.check_backup) rows.push({ key: "backup", label: "Backup" });
      if (this.check_power) rows.push({ key: "power", label: "Power" });
      if (this.check_allow_international) rows.push({ key: "allow_international", label: "Allow International" });
      if (this.check_apn) rows.push({ key: "apn", label: "APN" });
      if (this.check_gps) rows.push({ key: "gps", label: "GPS" });

      return rows;
    },
    modem_list() {
      return this.settings.modems;
    },
    check_stcp() {
      return this.settings.simcards?.stcp && Object.keys(this.settings.simcards?.stcp).length > 0;
    },
    check_gps() {
      return this.settings.simcards?.gps && Object.keys(this.settings.simcards?.gps).length > 0;
    },
    check_backup() {
      return this.settings.simcards?.backup && Object.keys(this.settings.simcards?.backup).length > 0;
    },
    check_allow_international() {
      return this.settings.simcards?.allow_international && Object.keys(this.settings.simcards?.allow_international).length > 0;
    },
    check_apn() {
      return this.settings.simcards?.apn && Object.keys(this.settings.simcards?.apn).length > 0;
    },
    check_power() {
      return this.settings.simcards?.power && Object.keys(this.settings.simcards?.power).length > 0;
    }
  },

  created() {
    this.settings = JSON.parse(JSON.stringify(this.network_settings));
  },

  watch:{
    settings:{
      deep: true,
      handler(){
        this.$emit("update_settings", this.settings)
      }
    }
  },

  methods: {
    gpsOptions(){
      return [
        {id: "gps", label: "gps"},
        {id: "gps_only", label: "gps_only"},
      ]
    },
    toggleSimple(key, modem) {
      this.settings.simcards[key][modem] = !this.settings.simcards[key][modem];
    },
    simcard_name(modem) {
      return this.settings.simcards_info[modem]
          ? `${modem} (${this.settings.simcards_info[modem]})`
          : `${modem}`;
    },

    toggleBackup(modem) {
      const isActive = this.settings.simcards.backup[modem];

      Object.keys(this.settings.simcards.backup).forEach(m => {
        this.settings.simcards.backup[m] = false;
      });

      if (!isActive) {
        this.settings.simcards.backup[modem] = true;
      }
    },

    apnOptions(modem) {
      const sim = this.settings.simcards_info[modem];
      if (!sim) {
        return [];
      }

      if (!this.settings.available_apns) return []

      const list = Object.values(this.settings.available_apns).map(apn => {
        if (apn.mno === this.settings.simcards_info[modem]) {
          return { id: apn.name, label: apn.name}
        }
      }).filter((element) => { return element !== undefined })
      return list ? list : [];
    },

    cancel() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.response_table {
  padding: 0;
  .filter.checkbox {
    min-width: auto;
  }
  .param_line{
    min-height: 50px;
  }
  .empty{
    height: 100px;
    margin-top: 0;
  }
}
</style>
